import * as React from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  Briefcase,
  Gavel,
  ArrowLeft,
  Hash,
  Star,
  ArrowUpRight,
  Check,
  Zap,
  Clock,
  RefreshCw,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  Archive,
  MoreHorizontal,
  Pencil,
  Square,
  Wallet,
  CalendarClock,
  Activity as ActivityIcon,
  Users,
  FolderOpen,
  MessageSquare,
  Scale,
  ClipboardList,
  Layers,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useAuth } from '@/features/auth';
import {
  useMatter,
  useNpsMutation,
  useLead,
  useLeadMutations,
  useQuickMatterMutations,
  useEmergencyMutations,
} from '../hooks';
import {
  NpsRatingDialog,
  CaseK3RatingDialog,
  GateTransitionDialog,
  ComplianceDialog,
  SuspendedCloseDialog,
  InteractionLogCard,
  CourtInstancesCard,
  CaseInstancesCard,
  DocumentsCard,
  MatterPlanCard,
  ChangeRequestsCard,
  MatterPhaseStepper,
  MatterTeamPanel,
  MatterReportButton,
  MatterOverviewPanel,
} from '../components';
import { CourtInfoCard } from '../components/MatterDetailCards';
import { NEXT_PHASE } from '../types';
import type { MatterPhase, MatterResult } from '../types';

type TabKey =
  | 'team'
  | 'documents'
  | 'interactions'
  | 'court'
  | 'caseInstances'
  | 'plan';

const TAB_KEYS: TabKey[] = [
  'team',
  'documents',
  'interactions',
  'court',
  'caseInstances',
  'plan',
];

const getResultBadgeClass = (result: MatterResult | undefined): string => {
  switch (result) {
    case 'WIN':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'LOSE':
      return 'bg-red-100 text-red-700 border-red-200';
    case 'SETTLED':
      return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    case 'CANCELLED':
      return 'bg-orange-100 text-orange-700 border-orange-200';
    default:
      return 'bg-slate-100 text-slate-700 border-slate-200';
  }
};

// Case 360° (Stage 6) — compliance / risk tones for the vitals strip.
const complianceTone = (status: string | undefined): string => {
  switch (status) {
    case 'PASSED':
    case 'PASSED_WITH_CONDITIONS':
      return 'text-green-600';
    case 'FAILED':
      return 'text-red-600';
    case 'BYPASSED':
      return 'text-amber-600';
    default:
      return 'text-slate-400';
  }
};

const riskTone = (level: string | undefined): string => {
  switch (level) {
    case 'LOW':
      return 'text-green-600';
    case 'MEDIUM':
      return 'text-yellow-600';
    case 'HIGH':
      return 'text-red-600';
    default:
      return 'text-slate-400';
  }
};

function VitalTile({
  icon: Icon,
  label,
  value,
  tone,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
  tone?: string;
  onClick?: (() => void) | undefined;
}) {
  const body = (
    <>
      <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
        <Icon className="h-3.5 w-3.5" />
        {label}
        {onClick && <Pencil className="ml-auto h-3 w-3 opacity-0 group-hover:opacity-60 transition-opacity" />}
      </div>
      <p className={cn('mt-1 truncate text-sm font-semibold', tone ?? 'text-slate-800')}>
        {value}
      </p>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="group rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-left shadow-sm transition-all hover:border-indigo-300 hover:shadow"
      >
        {body}
      </button>
    );
  }
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 shadow-sm">
      {body}
    </div>
  );
}

function BannerAction({
  children,
}: {
  children: React.ReactNode;
}) {
  return <div className="flex flex-wrap items-center gap-2">{children}</div>;
}

function getResultDisplay(result: MatterResult | undefined, isCourtCase: boolean): string {
  if (!result) return '';
  if (isCourtCase) return result;
  switch (result) {
    case 'WIN':
      return 'SUCCESS';
    case 'LOSE':
      return 'FAILED';
    default:
      return result;
  }
}

export function MatterDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasAccess, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: matter, isLoading, isError, refetch } = useMatter(id);
  const { npsMutation } = useNpsMutation();
  const { data: leadInfo } = useLead(id);
  const { acknowledgeMutation } = useLeadMutations();
  const { extendQuickMutation, convertQuickMutation } = useQuickMatterMutations();
  const { emergencyOpenMutation, emergencyExtendMutation } = useEmergencyMutations();

  const [suspendedDialogOpen, setSuspendedDialogOpen] = React.useState(false);
  const [npsDialogOpen, setNpsDialogOpen] = React.useState(false);
  const [caseK3DialogOpen, setCaseK3DialogOpen] = React.useState(false);
  const [gateDialogOpen, setGateDialogOpen] = React.useState(false);
  // 'transition' = normal forward FSM step; 'emergencyResolve' = Stage 5
  // PROVISIONAL_OPEN → PLANNING (same Gate 1 preview, resolve executor).
  const [gateMode, setGateMode] = React.useState<'transition' | 'emergencyResolve'>('transition');
  // Explicit gate target — lets an active Quick Advice (EXECUTION) open the
  // close gate directly instead of the legal next phase (REVIEW).
  const [gateToPhase, setGateToPhase] = React.useState<MatterPhase | undefined>(undefined);
  const [complianceDialogOpen, setComplianceDialogOpen] = React.useState(false);

  const isCourtCase = matter?.isCourtCase ?? false;
  // Case Instances tab is unlocked only for court cases on INSTANCE-billed contracts.
  const isInstanceBilled =
    isCourtCase && matter?.contract?.periodType === 'INSTANCE';
  // Team is the default tab — no `?tab=` query for it; unknown/stale values
  // (e.g. old `?tab=overview` links) and `court` on a non-court matter fall
  // back to it too.
  const requestedTab = searchParams.get('tab');
  const activeTab: TabKey =
    TAB_KEYS.includes(requestedTab as TabKey) &&
    !(requestedTab === 'court' && !isCourtCase) &&
    !(requestedTab === 'caseInstances' && !isInstanceBilled)
      ? (requestedTab as TabKey)
      : 'team';
  const setTab = (tab: TabKey) => {
    setSearchParams(tab === 'team' ? {} : { tab }, { replace: true });
  };

  const handleNpsSubmit = async (nps: number) => {
    if (matter) {
      await npsMutation.mutateAsync({ id: matter.id, nps });
      setNpsDialogOpen(false);
      const hasPeople =
        (matter.partners?.length ?? 0) > 0 ||
        (matter.responsibleAttorneys?.length ?? 0) > 0 ||
        (matter.staffs?.length ?? 0) > 0;
      if (hasPeople) {
        setCaseK3DialogOpen(true);
      } else {
        void refetch();
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-white border-b border-slate-200 px-6 py-6">
          <div className="max-w-7xl mx-auto space-y-5">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-9 w-48" />
            </div>
            <div className="flex items-center gap-4">
              <Skeleton className="h-14 w-14 rounded-2xl" />
              <div className="space-y-2">
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-5 w-96" />
              </div>
            </div>
            <Skeleton className="h-10 w-full max-w-2xl" />
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
          <Skeleton className="h-72" />
        </div>
      </div>
    );
  }

  if (isError || !matter) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
            <div className="text-red-500 mb-4">
              <Briefcase className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="text-lg font-medium">{t('matterNotFound')}</p>
              <p className="text-sm text-slate-500 mt-1">{t('matterNotFoundDescription')}</p>
            </div>
            <Link to="/app/matters">
              <Button variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('backToMatters')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const canClose = hasAccess('matter', 'close');
  const canTransition = hasAccess('matter', 'transition');
  const canUpdate = hasAccess('matter', 'update');
  const canReport = hasAccess('matter', 'report');
  // Case 360° (Stage 6) — Partner+ may set the Compliance decision / Risk Level
  // while the matter is open (most relevant in INTAKE, but risk can change).
  const canSetCompliance =
    hasAccess('matter', 'compliance') && matter.phase !== 'CLOSED';
  const nextPhase = matter.phase ? NEXT_PHASE[matter.phase] : undefined;

  const currentUserId = user?.id;
  const globalRoleType = user?.role?.type;
  const isTeamPartner = matter.partners?.some((p) => p.id === currentUserId) ?? false;

  // Case 360° (Stage 2) — Lead acknowledge call-to-action.
  // `matter:manage-all` = firm-wide case-management scope (Office Manager);
  // the server-side gates accept it alongside the senior role types.
  const isPartnerOrMD =
    globalRoleType === 'MD' ||
    globalRoleType === 'PARTNER' ||
    globalRoleType === 'MANAGER' ||
    hasAccess('matter', 'manage-all') ||
    isTeamPartner;
  const pendingDelegation = leadInfo?.pendingDelegation ?? null;
  const amIncomingLead =
    !!pendingDelegation &&
    !pendingDelegation.acknowledged &&
    pendingDelegation.toUserId === currentUserId;

  // Case 360° (Stage 4) — Quick Advice clock. "Quick mode" is active only while
  // the consultation clock runs; convert clears `quickDeadline`.
  const isQuickMatter =
    matter.category === 'QA' && !!matter.quickDeadline && matter.phase !== 'CLOSED';
  const quickExtensions = matter.quickExtensions ?? 0;
  const canExtendQuick = isQuickMatter && canUpdate && quickExtensions < 2;
  const canConvertQuick = isQuickMatter && canTransition && isPartnerOrMD;
  const quickDeadlineDate = matter.quickDeadline ? new Date(matter.quickDeadline) : null;
  const quickDaysLeft = quickDeadlineDate
    ? Math.ceil((quickDeadlineDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  // Case 360° (Stage 5) — Emergency Mode & SUSPENDED controls (Partner/MD).
  const isEmergency = matter.phase === 'PROVISIONAL_OPEN';
  const isSuspended = matter.phase === 'SUSPENDED';
  const canEmergencyOpen = matter.phase === 'INTAKE' && canTransition && isPartnerOrMD;
  const emergencyExtensions = matter.emergencyExtensions ?? 0;
  const canEmergencyExtend =
    isEmergency && canTransition && isPartnerOrMD && emergencyExtensions < 2;
  const canEmergencyResolve = isEmergency && canTransition && isPartnerOrMD;
  const canArchiveSuspended = isSuspended && canTransition && isPartnerOrMD;
  const emergencyExpiresDate = matter.emergencyExpiresAt
    ? new Date(matter.emergencyExpiresAt)
    : null;
  const emergencyHoursLeft = emergencyExpiresDate
    ? Math.ceil((emergencyExpiresDate.getTime() - Date.now()) / (1000 * 60 * 60))
    : null;

  // Case 360° — closing goes ONLY through the Gate dialog (result picker
  // inside; the close endpoint executes). A full matter closes via Gate 4
  // (REVIEW, the stepper's "Advance to Closed"); an active Quick Advice closes
  // straight from EXECUTION via its own button (gate-check returns no blocks).
  const showQuickClose = isQuickMatter && canClose && !matter.result;
  // Quick-mode matters don't run the normal forward ladder — they close or
  // convert — so hide the generic "Advance to {phase}" gate button for them.
  // Advancing to CLOSED executes the close endpoint, so it needs matter:close.
  const showAdvanceGate =
    canTransition &&
    !!nextPhase &&
    !isQuickMatter &&
    !isSuspended &&
    (nextPhase !== 'CLOSED' || canClose);

  const isClosed = matter.phase === 'CLOSED';

  const tabs: { key: TabKey; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { key: 'team', label: t('matterTabs.team'), icon: Users },
    { key: 'documents', label: t('matterTabs.documents'), icon: FolderOpen },
    { key: 'interactions', label: t('matterTabs.interactions'), icon: MessageSquare },
    ...(isCourtCase
      ? [{ key: 'court' as TabKey, label: t('matterTabs.court'), icon: Scale }]
      : []),
    ...(isInstanceBilled
      ? [
          {
            key: 'caseInstances' as TabKey,
            label: t('matterTabs.caseInstances'),
            icon: Layers,
          },
        ]
      : []),
    { key: 'plan', label: t('matterTabs.plan'), icon: ClipboardList },
  ];

  const billingStatus = matter?.billingStatus ?? 'allowed';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ---- Header ---------------------------------------------------- */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4">
          {/* Top row: back + actions */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Link
              to="/app/matters"
              className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              {t('backToMatters')}
            </Link>

            <div className="flex flex-wrap items-center gap-2">
              {showQuickClose && (
                <Button
                  className="bg-red-500 text-white hover:bg-red-600"
                  onClick={() => {
                    setGateMode('transition');
                    setGateToPhase('CLOSED');
                    setGateDialogOpen(true);
                  }}
                >
                  <Square className="mr-2 h-4 w-4" />
                  {t('closeMatter')}
                </Button>
              )}
              {canConvertQuick && (
                <Button
                  className="bg-emerald-600 text-white hover:bg-emerald-700"
                  onClick={async () => {
                    await convertQuickMutation.mutateAsync({ id: matter.id });
                    void refetch();
                  }}
                  disabled={convertQuickMutation.isPending}
                >
                  <Zap className="mr-2 h-4 w-4" />
                  {convertQuickMutation.isPending
                    ? t('processing')
                    : t('quickAdvice.convert')}
                </Button>
              )}
              {canReport && <MatterReportButton matter={matter} />}
              {(canUpdate || canSetCompliance || canEmergencyOpen) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" aria-label={t('quickActions')}>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {canUpdate && (
                      <DropdownMenuItem
                        onClick={() => navigate(`/app/matters/${matter.id}/edit`)}
                      >
                        <Pencil className="mr-2 h-4 w-4" />
                        {t('editMatter')}
                      </DropdownMenuItem>
                    )}
                    {canSetCompliance && (
                      <DropdownMenuItem onClick={() => setComplianceDialogOpen(true)}>
                        <ShieldCheck className="mr-2 h-4 w-4" />
                        {t('compliance.reviewButton')}
                      </DropdownMenuItem>
                    )}
                    {canEmergencyOpen && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-orange-600 focus:text-orange-700"
                          onClick={async () => {
                            await emergencyOpenMutation.mutateAsync({ id: matter.id });
                            void refetch();
                          }}
                          disabled={emergencyOpenMutation.isPending}
                        >
                          <AlertTriangle className="mr-2 h-4 w-4" />
                          {t('emergency.openButton')}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          {/* Title row */}
          <div className="mt-4 flex items-start gap-4">
            <div
              className={cn(
                'flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl shadow-sm',
                isSuspended
                  ? 'bg-slate-100 text-slate-500'
                  : isEmergency
                    ? 'bg-orange-100 text-orange-600'
                    : isClosed
                      ? 'bg-slate-100 text-slate-500'
                      : 'bg-indigo-50 text-indigo-600',
              )}
            >
              {isCourtCase ? <Gavel className="h-7 w-7" /> : <Briefcase className="h-7 w-7" />}
            </div>

            <div className="min-w-0 flex-1">
              <h1 className="truncate text-2xl font-bold text-slate-900">
                {matter.name || matter.caseNumber || t('unnamedMatter')}
              </h1>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                  <Hash className="h-3 w-3" />
                  {String(matter.id).padStart(5, '0')}
                </span>
                {matter.category && matter.category !== 'MIGRATION_PENDING' && (
                  <Badge variant="outline" className="text-xs font-medium">
                    {t(`matterCategory.${matter.category}`)}
                  </Badge>
                )}
                <Badge
                  variant={isCourtCase ? 'default' : 'outline'}
                  className="text-xs font-medium"
                >
                  {isCourtCase ? t('courtCase') : t('nonCourtCase')}
                </Badge>
                {matter.result && (
                  <Badge className={`${getResultBadgeClass(matter.result)} border text-xs`}>
                    {getResultDisplay(matter.result, isCourtCase)}
                  </Badge>
                )}
                {matter.closureType && matter.closureType !== 'NORMAL' && (
                  <Badge className="border border-slate-300 bg-slate-100 text-slate-700 text-xs">
                    <Archive className="mr-1 h-3 w-3" />
                    {t(`emergency.closureType.${matter.closureType}`)}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Lifecycle stepper + advance action */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 py-3">
            <MatterPhaseStepper phase={matter.phase} />
            {showAdvanceGate && (
              <Button
                className="bg-indigo-600 text-white hover:bg-indigo-700"
                onClick={() => {
                  setGateMode('transition');
                  setGateToPhase(nextPhase);
                  setGateDialogOpen(true);
                }}
              >
                <ArrowUpRight className="mr-2 h-4 w-4" />
                {t('gate.advanceTo', { phase: t(`phase.${nextPhase}`) })}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-5 pb-24">
        {/* ---- Contextual banners --------------------------------------- */}
        <div className="space-y-3">
          {/* Quick Advice clock */}
          {isQuickMatter && quickDeadlineDate && (
            <div
              className={cn(
                'flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3',
                (quickDaysLeft ?? 0) <= 2
                  ? 'border-red-200 bg-red-50'
                  : 'border-amber-200 bg-amber-50',
              )}
            >
              <div className="flex items-center gap-3">
                <Clock
                  className={cn(
                    'h-5 w-5 shrink-0',
                    (quickDaysLeft ?? 0) <= 2 ? 'text-red-500' : 'text-amber-500',
                  )}
                />
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    {t('quickAdvice.newQuickAdvice')} —{' '}
                    {t('quickAdvice.deadlineBadge', {
                      days: Math.max(quickDaysLeft ?? 0, 0),
                    })}
                  </p>
                  <p className="text-xs text-slate-500">
                    {format(quickDeadlineDate, 'MMM d, yyyy')}
                    {quickExtensions > 0
                      ? ` · ${t('quickAdvice.extensionsUsed', { count: quickExtensions })}`
                      : ''}
                  </p>
                </div>
              </div>
              {canExtendQuick && (
                <BannerAction>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-amber-300 text-amber-700 hover:bg-amber-100"
                    onClick={async () => {
                      await extendQuickMutation.mutateAsync({ id: matter.id });
                      void refetch();
                    }}
                    disabled={extendQuickMutation.isPending}
                  >
                    <RefreshCw className="mr-2 h-3.5 w-3.5" />
                    {extendQuickMutation.isPending
                      ? t('processing')
                      : t('quickAdvice.extend')}
                  </Button>
                </BannerAction>
              )}
            </div>
          )}

          {/* Emergency window */}
          {isEmergency && emergencyExpiresDate && (
            <div
              className={cn(
                'flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3',
                (emergencyHoursLeft ?? 0) <= 12
                  ? 'border-red-200 bg-red-50'
                  : 'border-orange-200 bg-orange-50',
              )}
            >
              <div className="flex items-center gap-3">
                <AlertTriangle
                  className={cn(
                    'h-5 w-5 shrink-0',
                    (emergencyHoursLeft ?? 0) <= 12 ? 'text-red-500' : 'text-orange-500',
                  )}
                />
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    {t('phase.PROVISIONAL_OPEN')} —{' '}
                    {t('emergency.windowBadge', {
                      hours: Math.max(emergencyHoursLeft ?? 0, 0),
                    })}
                  </p>
                  <p className="text-xs text-slate-500">
                    {format(emergencyExpiresDate, 'MMM d, yyyy HH:mm')}
                    {emergencyExtensions > 0
                      ? ` · ${t('emergency.extensionsUsed', { count: emergencyExtensions })}`
                      : ''}
                  </p>
                </div>
              </div>
              <BannerAction>
                {canEmergencyExtend && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-orange-300 text-orange-700 hover:bg-orange-100"
                    onClick={async () => {
                      await emergencyExtendMutation.mutateAsync({ id: matter.id });
                      void refetch();
                    }}
                    disabled={emergencyExtendMutation.isPending}
                  >
                    <RefreshCw className="mr-2 h-3.5 w-3.5" />
                    {emergencyExtendMutation.isPending
                      ? t('processing')
                      : t('emergency.extendButton')}
                  </Button>
                )}
                {canEmergencyResolve && (
                  <Button
                    size="sm"
                    className="bg-indigo-600 text-white hover:bg-indigo-700"
                    onClick={() => {
                      // Gate 1 dialog previews the blocks (compliance / risk /
                      // partner); confirm executes the emergency resolve.
                      setGateMode('emergencyResolve');
                      setGateToPhase('PLANNING');
                      setGateDialogOpen(true);
                    }}
                  >
                    <ArrowUpRight className="mr-2 h-3.5 w-3.5" />
                    {t('emergency.resolveButton')}
                  </Button>
                )}
              </BannerAction>
            </div>
          )}

          {/* Suspended */}
          {isSuspended && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-300 bg-slate-100 px-4 py-3">
              <div className="flex items-center gap-3">
                <ShieldAlert className="h-5 w-5 shrink-0 text-slate-500" />
                <p className="text-sm font-medium text-slate-700">
                  {t('matterDetail.suspendedBanner')}
                </p>
              </div>
              {canArchiveSuspended && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-slate-300 text-slate-700 hover:bg-slate-200"
                  onClick={() => setSuspendedDialogOpen(true)}
                >
                  <Archive className="mr-2 h-3.5 w-3.5" />
                  {t('emergency.archiveButton')}
                </Button>
              )}
            </div>
          )}

          {/* Lead handoff awaiting MY acknowledge */}
          {amIncomingLead && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 shrink-0 text-amber-500" />
                <p className="text-sm font-medium text-slate-800">
                  {t('matterDetail.acknowledgeBanner')}
                </p>
              </div>
              <Button
                size="sm"
                className="bg-amber-500 text-white hover:bg-amber-600"
                onClick={async () => {
                  await acknowledgeMutation.mutateAsync({ id: matter.id });
                  void refetch();
                }}
                disabled={acknowledgeMutation.isPending}
              >
                <Check className="mr-2 h-3.5 w-3.5" />
                {acknowledgeMutation.isPending
                  ? t('processing')
                  : t('leadDelegation.acknowledgeButton')}
              </Button>
            </div>
          )}

          {/* Closed — post-close ratings */}
          {isClosed && matter.nps == null && canClose && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
              <div className="flex items-center gap-3">
                <Star className="h-5 w-5 shrink-0 text-blue-500" />
                <p className="text-sm font-medium text-slate-800">
                  {t('matterDetail.closedBanner')}
                </p>
              </div>
              <BannerAction>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  onClick={() => setNpsDialogOpen(true)}
                >
                  <Star className="mr-2 h-3.5 w-3.5" />
                  {t('nps.rateNps')}
                </Button>
              </BannerAction>
            </div>
          )}
        </div>

        {/* ---- Vitals strip ---------------------------------------------- */}
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <VitalTile
            icon={ShieldCheck}
            label={t('matterDetail.vitals.compliance')}
            value={
              matter.compliance
                ? t(`compliance.status.${matter.compliance}`)
                : '—'
            }
            tone={complianceTone(matter.compliance)}
            onClick={canSetCompliance ? () => setComplianceDialogOpen(true) : undefined}
          />
          <VitalTile
            icon={ShieldAlert}
            label={t('matterDetail.vitals.risk')}
            value={
              matter.riskLevel && ['LOW', 'MEDIUM', 'HIGH'].includes(matter.riskLevel)
                ? t(`compliance.risk.${matter.riskLevel}`)
                : '—'
            }
            tone={riskTone(matter.riskLevel)}
            onClick={canSetCompliance ? () => setComplianceDialogOpen(true) : undefined}
          />
          <VitalTile
            icon={ActivityIcon}
            label={t('matterDetail.vitals.billing')}
            value={t(`matterDetail.billingStatus.${billingStatus}`)}
            tone={
              billingStatus === 'allowed'
                ? 'text-green-600'
                : billingStatus === 'tracking_only'
                  ? 'text-amber-600'
                  : 'text-slate-500'
            }
          />
          <VitalTile
            icon={Wallet}
            label={t('matterDetail.vitals.budget')}
            value={
              matter.budget
                ? new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    // The budget (billing cap) is in the contract's currency.
                    currency: matter?.contract?.currency ?? 'USD',
                    minimumFractionDigits: 0,
                  }).format(matter.budget)
                : '—'
            }
          />
          <VitalTile
            icon={CalendarClock}
            label={t('matterDetail.vitals.targetDate')}
            value={
              matter.targetEndDate
                ? format(new Date(matter.targetEndDate), 'MMM d, yyyy')
                : '—'
            }
          />
        </div>

        {/* ---- Overview (always visible, above the tabs) ------------------ */}
        <div className="mt-4">
          <MatterOverviewPanel matter={matter} />
        </div>

        {/* ---- Tabs ------------------------------------------------------ */}
        <div className="mt-6 flex flex-wrap gap-1 border-b border-slate-200">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={cn(
                '-mb-px inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
                activeTab === key
                  ? 'border-indigo-600 text-indigo-700'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-800',
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        <div className="py-6">
          {activeTab === 'team' && (
            <MatterTeamPanel matter={matter} onChanged={() => void refetch()} />
          )}

          {activeTab === 'documents' && (
            <div className="max-w-4xl">
              <DocumentsCard matter={matter} />
            </div>
          )}

          {activeTab === 'interactions' && (
            <div className="max-w-4xl">
              <InteractionLogCard matter={matter} />
            </div>
          )}

          {activeTab === 'court' && isCourtCase && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <CourtInfoCard matter={matter} />
              <CourtInstancesCard matter={matter} />
            </div>
          )}

          {activeTab === 'caseInstances' && isInstanceBilled && (
            <div className="grid grid-cols-1 gap-6">
              <CaseInstancesCard matter={matter} />
            </div>
          )}

          {activeTab === 'plan' && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <MatterPlanCard matter={matter} />
              <ChangeRequestsCard matter={matter} />
            </div>
          )}
        </div>
      </div>

      {/* ---- Dialogs ----------------------------------------------------- */}
      {/* NPS Rating Dialog (K2) */}
      <NpsRatingDialog
        open={npsDialogOpen}
        onOpenChange={(open) => {
          setNpsDialogOpen(open);
          if (!open) void refetch();
        }}
        matter={matter}
        onSubmit={handleNpsSubmit}
        isLoading={npsMutation.isPending}
      />

      {/* Case Team Rating Dialog (K3) */}
      <CaseK3RatingDialog
        open={caseK3DialogOpen}
        onOpenChange={(open) => {
          setCaseK3DialogOpen(open);
          if (!open) void refetch();
        }}
        matter={matter ?? null}
        onSuccess={() => void refetch()}
      />

      {/* Case 360° — Quality Gate transition dialog (closing runs through it
          too: a CLOSED target shows the result select and executes the close
          endpoint; success chains into the NPS rating) */}
      <GateTransitionDialog
        open={gateDialogOpen}
        onOpenChange={(open) => {
          setGateDialogOpen(open);
          if (!open) {
            setGateToPhase(undefined);
            void refetch();
          }
        }}
        matter={matter ?? null}
        toPhase={gateMode === 'emergencyResolve' ? 'PLANNING' : (gateToPhase ?? nextPhase)}
        mode={gateMode}
        onSuccess={() => {
          const wasClose =
            gateMode === 'transition' && (gateToPhase ?? nextPhase) === 'CLOSED';
          void refetch();
          if (wasClose) setNpsDialogOpen(true);
        }}
      />

      {/* Case 360° (Stage 6) — Compliance & Risk decision dialog */}
      <ComplianceDialog
        open={complianceDialogOpen}
        onOpenChange={setComplianceDialogOpen}
        matterId={matter?.id ?? null}
        onSuccess={() => void refetch()}
      />

      {/* Case 360° (Stage 5) — archive a SUSPENDED matter */}
      <SuspendedCloseDialog
        open={suspendedDialogOpen}
        onOpenChange={setSuspendedDialogOpen}
        matterId={matter?.id ?? null}
        onSuccess={() => void refetch()}
      />
    </div>
  );
}
